/* /app/frontend/src/copy/dealRoom.js — iter-362
 *
 * Central copy dictionary for every user-facing string that shows
 * up in the Deal Room. Keeping this in one place means:
 *
 *   1. **Consistency** — "Waiting 3 days" is spelled the same
 *      everywhere (Waiting-On row, milestone chip, Participants
 *      row, Communication summary strip).
 *   2. **Reviewability** — a product / copy pass touches ONE file
 *      instead of chasing 40 inline strings across a 5,600-line
 *      component.
 *   3. **Future i18n** — every string here is a candidate for a
 *      translation key on day one.
 *
 * Convention: functions accept plain data (never React nodes)
 * and return plain strings.
 */

const _DAY_MS = 86_400_000;

/* Terminal transaction states. When `deal.status` matches one of
 * these, the Deal Room collapses "Next Best Action" and
 * "Waiting On" into a calm completion banner. */
export const TERMINAL_STATUSES = new Set([
  "Exchanged",
  "Completed",
  "Fallen Through",
]);

export const TERMINAL_LABEL = {
  Exchanged:       "Exchanged",
  Completed:       "Completed",
  "Fallen Through": "Fallen through",
};

/* Verb-first sentence for the completion banner. Reads calmly:
 *   "Transaction completed on 4 Jul 2026"
 *   "Contracts exchanged on 27 Aug 2026"
 *   "Transaction fell through on 12 Aug 2026" */
export function terminalBanner(status, iso) {
  const date = iso ? formatLongDate(iso) : null;
  const on = date ? ` on ${date}` : "";
  switch (status) {
    case "Exchanged":       return `Contracts exchanged${on}`;
    case "Completed":       return `Transaction completed${on}`;
    case "Fallen Through":  return `Transaction fell through${on}`;
    default:                return `Transaction ${String(status || "").toLowerCase()}${on}`;
  }
}

/* Standard "Waiting X days" chip.
 *   0 or negative days  → "Waiting today"
 *   1 day               → "Waiting 1 day"
 *   otherwise           → "Waiting {n} days"
 *
 * Returns `null` when the deal has no waiting context (fresh row,
 * terminal state, etc.) so callers can suppress the chip
 * altogether rather than rendering "Waiting — days". */
export function waitingChip(days) {
  if (days === null || days === undefined || Number.isNaN(days)) return null;
  const d = Math.max(0, Math.round(days));
  if (d === 0) return "Waiting today";
  if (d === 1) return "Waiting 1 day";
  return `Waiting ${d} days`;
}

/* Days since an ISO timestamp — the canonical helper for
 * "Waiting X days" chips + milestone age labels. Returns null
 * when the input can't be parsed so callers can suppress the
 * chip rather than render "NaN days". */
export function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor((Date.now() - ms) / _DAY_MS));
}

/* Long British date — e.g. "4 Jul 2026". Preserves the same
 * `en-GB, day+shortMonth+year` format used across the app. */
export function formatLongDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

/* Standard health-status vocabulary. Sourced from the SSOT hook
 * so every panel (Health chip on the sidebar, hero pill, Journey
 * "At Risk" markers) reads the same word. */
export const HEALTH_COPY = {
  healthy:  { label: "Healthy",  tone: "emerald", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  steady:   { label: "Steady",   tone: "sky",     chip: "bg-sky-50 text-sky-700 ring-sky-200" },
  at_risk:  { label: "At risk",  tone: "amber",   chip: "bg-amber-50 text-amber-700 ring-amber-200" },
  blocked:  { label: "Blocked",  tone: "rose",    chip: "bg-rose-50 text-rose-700 ring-rose-200" },
  complete: { label: "Complete", tone: "slate",   chip: "bg-slate-100 text-slate-600 ring-slate-200" },
};

/* Standard status pill copy for the deal ribbon. */
export const DEAL_STATUS_COPY = {
  Active:           "Active",
  "At Risk":        "At risk",
  Exchanged:        "Exchanged",
  Completed:        "Completed",
  "Fallen Through": "Fallen through",
};

/* Owner-role labels — used on milestone cards + Waiting On
 * rows. Keeps the vocabulary aligned with the Participants rail. */
export const OWNER_ROLE_COPY = {
  buyer:            "Buyer",
  seller:           "Seller",
  buyer_solicitor:  "Buyer solicitor",
  seller_solicitor: "Seller solicitor",
  mortgage_broker:  "Mortgage broker",
  negotiator:       "Lead agent",
  agent:            "Lead agent",
};

/* "No blocker logged" placeholder — matches the tone used
 * elsewhere in the ChainFlow design language (calm, factual). */
export const BLOCKER_COPY = {
  none: "No blocker logged",
  auto: "Auto-escalated",
};
